export { startAdBlockerDetection, getAdBlockerResult, resetForTests };

// Detección de bloqueador de anuncios/privacidad para adjuntarla al
// custom_data de los eventos de analytics. Dos señales independientes, porque
// no todos los bloqueadores actúan igual:
//
// 1) Bait de RED: petición no-cors al bait clásico de googlesyndication,
//    bloqueado por la regla de EasyList ||pagead2.googlesyndication.com^
//    (verificado contra las listas reales el 4/8/2026 — ninguna URL propia
//    matchea, esta sí en cualquier bloqueador). Caza a los que CORTAN la
//    petición: extensiones en modo block y bloqueo DNS (Pi-hole y compañía,
//    que no hacen filtrado cosmético y serían invisibles para la señal 2).
//    Limitación: los bloqueadores con reglas redirect/surrogate (uBlock
//    `redirect=`, extensiones MV3 con declarativeNetRequest, AdGuard de
//    sistema) sirven un stub y el fetch RESUELVE — para la probe parecen
//    tráfico limpio. En no-cors la respuesta es opaca (sin status, url ni
//    redirected), así que no hay forma de endurecer esta señal por sí sola.
//
// 2) Bait DOM: elemento con clases que las reglas genéricas de element hiding
//    de EasyList ocultan (##.clase). El filtrado cosmético va siempre activo
//    en los bloqueadores de extensión/sistema con content scripts, incluidos
//    los de redirect → esta señal caza justo lo que a la 1 se le escapa.
//    Funciona incluso sin red. Limitación: los bloqueadores solo-DNS no la
//    disparan (para eso está la señal 1), y los MV3 permission-less en modo
//    básico (uBO Lite Basic, verificado el 5/8/2026) tampoco: no inyectan
//    content scripts → no hay filtrado cosmético que medir.
//
// 3) Probe de REDIRECT: fetch cors con redirect:"manual" al mismo bait. Los
//    redirects de bloqueador (DNR en MV3, webRequest en MV2) son redirects
//    reales a chrome-extension://… a nivel de red → la respuesta llega como
//    type "opaqueredirect". Google sirve la URL con 200 directo y
//    access-control-allow-origin:* (verificado el 5/8/2026) → sin bloqueador
//    resuelve normal. Si el fetch RECHAZA no se afirma nada (podría ser un
//    cambio de cabeceras de Google, y el corte lo cubre ya la señal 1): solo
//    el opaqueredirect afirma bloqueo. Esta señal caza a uBO Lite Basic, que
//    es invisible para las otras dos.
//
// Semántica combinada: cualquiera de las tres detecta → true. false cuando la
// red respondió limpia (provisional hasta el último check DOM; el flip
// false→true es raro porque el CSS cosmético se inyecta a document_start y el
// primer check ya lo ve). Ambigüedades → null (desconocido): sin red no se
// puede afirmar limpio y un timeout no es un bloqueo. El resultado se cachea
// a nivel de página; se arranca en el boot del bundle para que esté listo
// antes del onPageView (el init tiene por medio el roundtrip de config).
const BAIT_URL =
  "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
const TIMEOUT_MS = 3000;

// Clases con regla genérica exacta en EasyList (##.ad-slot, ##.ad_placement,
// ##.AdContainer, ##.ad-banner-300x250), verificadas contra la lista real el
// 5/8/2026. Varias reglas independientes a propósito: una exception rule
// puntual sobre una de ellas no anula el bait entero.
const DOM_BAIT_CLASSES = "ad-slot ad_placement AdContainer ad-banner-300x250";
// El CSS genérico inyectado a document_start oculta el bait nada más
// insertarlo; los bloqueadores que procesan nodos nuevos vía observer tardan
// un tick. Primer check temprano + último check de cierre.
const DOM_BAIT_CHECK_DELAYS_MS = [150, 1200];

let result: boolean | null = null;
let started = false;
// Veredictos por señal: true = bloqueado, false = limpio, null = pendiente o
// no concluyente.
let networkVerdict: boolean | null = null;
let domVerdict: boolean | null = null;
let redirectVerdict: boolean | null = null;
let baitElement: HTMLElement | null = null;
let pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

function startAdBlockerDetection(): void {
  if (started) {
    return;
  }
  started = true;
  try {
    startNetworkProbe();
  } catch (e) {
    // jamás romper el arranque por telemetría
  }
  try {
    startRedirectProbe();
  } catch (e) {
    // jamás romper el arranque por telemetría
  }
  try {
    startDomBait();
  } catch (e) {
    // jamás romper el arranque por telemetría
  }
}

function getAdBlockerResult(): boolean | null {
  return result;
}

function resetForTests(): void {
  result = null;
  started = false;
  networkVerdict = null;
  domVerdict = null;
  redirectVerdict = null;
  pendingTimeouts.forEach((id) => clearTimeout(id));
  pendingTimeouts = [];
  removeBait();
}

function startNetworkProbe(): void {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    // Sin red la probe no afirma nada; el bait DOM sigue valiendo (es local).
    return;
  }
  const timeout = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), TIMEOUT_MS),
  );
  const probe = fetch(BAIT_URL, {
    method: "HEAD",
    mode: "no-cors",
    cache: "no-store",
  })
    .then(() => false)
    .catch(() => true);
  Promise.race([probe, timeout])
    .then((value) => {
      networkVerdict = value;
      combineVerdicts();
    })
    .catch(() => {});
}

function startRedirectProbe(): void {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return;
  }
  fetch(BAIT_URL, {
    method: "HEAD",
    mode: "cors",
    redirect: "manual",
    cache: "no-store",
  })
    .then((response) => {
      redirectVerdict = response.type === "opaqueredirect";
      combineVerdicts();
    })
    .catch(() => {
      // Rechazo ambiguo (¿cabeceras CORS de Google? ¿corte?): no afirmar. El
      // corte de red ya lo cubre la probe no-cors.
    });
}

function startDomBait(): void {
  if (typeof document === "undefined") {
    // Sin DOM (contexto degenerado): la señal no aplica; con "sin evidencia"
    // el contrato queda igual que el histórico de solo-red.
    domVerdict = false;
    combineVerdicts();
    return;
  }
  if (document.body) {
    insertDomBait();
    return;
  }
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      try {
        insertDomBait();
      } catch (e) {
        // jamás romper el arranque por telemetría
      }
    },
    { once: true },
  );
}

function insertDomBait(): void {
  const bait = document.createElement("div");
  bait.className = DOM_BAIT_CLASSES;
  bait.setAttribute("aria-hidden", "true");
  // 1x1 fuera de viewport: real para el bloqueador, invisible para el
  // usuario. OJO: no fijar display/visibility inline — es justo lo que se
  // mide después.
  bait.style.cssText =
    "position:absolute;top:-9999px;left:-9999px;width:1px;height:1px;";
  document.body.appendChild(bait);
  baitElement = bait;
  DOM_BAIT_CHECK_DELAYS_MS.forEach((delay, index) => {
    const isLastCheck = index === DOM_BAIT_CHECK_DELAYS_MS.length - 1;
    pendingTimeouts.push(setTimeout(() => checkDomBait(isLastCheck), delay));
  });
}

function checkDomBait(isLastCheck: boolean): void {
  if (domVerdict !== null || baitElement === null) {
    return;
  }
  if (isBaitHidden(baitElement)) {
    domVerdict = true;
  } else if (isLastCheck) {
    domVerdict = false;
  }
  if (domVerdict !== null) {
    removeBait();
    combineVerdicts();
  }
}

function isBaitHidden(bait: HTMLElement): boolean {
  if (!bait.isConnected) {
    // Algunos scriptlets eliminan el nodo en vez de ocultarlo.
    return true;
  }
  // El filtrado cosmético oculta vía display:none!important (a veces
  // visibility) en hoja inyectada, y eso sí se refleja en getComputedStyle.
  // No usamos offsetHeight/offsetParent: en happy-dom no hay layout y en
  // browser no aportan sobre display/visibility.
  const style = window.getComputedStyle(bait);
  return style.display === "none" || style.visibility === "hidden";
}

function removeBait(): void {
  if (baitElement !== null) {
    baitElement.remove();
    baitElement = null;
  }
}

function combineVerdicts(): void {
  if (
    networkVerdict === true ||
    domVerdict === true ||
    redirectVerdict === true
  ) {
    result = true;
    return;
  }
  if (networkVerdict === false) {
    // Limpio para la red; provisional mientras el bait DOM no cierre (si
    // luego detecta, el resultado sube a true — refinar hacia true siempre es
    // correcto).
    result = false;
  }
}
